import React, { useState } from "react";
import { TreatmentPlanSessionListItem, SessionStatus, WIZARD_STEP_LABELS, WizardStep } from "../types";
import CreateSessionModal from "./CreateSessionModal";
import ConfirmModal from "./ConfirmModal";
import "./ClientSelector.css";

interface ClientSelectorProps {
  sessions: TreatmentPlanSessionListItem[];
  isLoading: boolean;
  onCreateSession: (clientName: string, dateOfBirth: string | null) => Promise<void>;
  onResumeSession: (sessionId: number) => void;
  onDeleteSession: (sessionId: number) => void;
}

const ClientSelector: React.FC<ClientSelectorProps> = ({
  sessions,
  isLoading,
  onCreateSession,
  onResumeSession,
  onDeleteSession,
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);

  const handleDeleteClick = (e: React.MouseEvent, session: TreatmentPlanSessionListItem) => {
    e.stopPropagation();
    setDeleteTarget({ id: session.id, name: session.client_name });
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      onDeleteSession(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case SessionStatus.COMPLETED:
        return "status-badge--completed";
      case SessionStatus.IN_PROGRESS:
        return "status-badge--in-progress";
      case SessionStatus.ARCHIVED:
        return "status-badge--archived";
      default:
        return "status-badge--draft";
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString();
  };

  const getStepLabel = (step: number) => {
    return WIZARD_STEP_LABELS[step as WizardStep] || `Step ${step}`;
  };

  return (
    <div className="sessions-list-view">
      <div className="sessions-container">
        <div className="sessions-list-header">
          <div className="sessions-list-header__left">
            <h2>Treatment Plans</h2>
            <span className="sessions-count">{sessions.length} session(s)</span>
          </div>
          <button
            className="add-session-btn"
            onClick={() => setShowCreateModal(true)}
          >
            + Add Session
          </button>
        </div>

        <div className="sessions-table-wrapper">
        {isLoading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading sessions...</p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📋</span>
            <h3>No Treatment Plans</h3>
            <p>Get started by creating your first treatment plan session.</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              + Create First Session
            </button>
          </div>
        ) : (
          <table className="sessions-table">
            <thead>
              <tr>
                <th className="col-client">Client Name</th>
                <th className="col-dob">Date of Birth</th>
                <th className="col-status">Status</th>
                <th className="col-step">Current Step</th>
                <th className="col-updated">Last Updated</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr
                  key={session.id}
                  className="session-row"
                >
                  <td className="col-client">
                    <span className="client-name">{session.client_name}</span>
                  </td>
                  <td className="col-dob">{formatDate(session.date_of_birth)}</td>
                  <td className="col-status">
                    <span className={`status-badge ${getStatusBadgeClass(session.status)}`}>
                      {session.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="col-step">
                    <span className="step-indicator">
                      <span className="step-number">{session.current_step}</span>
                      <span className="step-label">{getStepLabel(session.current_step)}</span>
                    </span>
                  </td>
                  <td className="col-updated">
                    {formatDateTime(session.updated_at || session.created_at)}
                  </td>
                  <td className="col-actions">
                    <div className="action-buttons">
                      <button
                        className="btn-icon btn-open"
                        onClick={() => onResumeSession(session.id)}
                        title="Open session"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={(e) => handleDeleteClick(e, session)}
                        title="Delete session"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>
      </div>

      <CreateSessionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateSession={onCreateSession}
      />

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Session"
        message={`Are you sure you want to delete the session for "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default ClientSelector;
